import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      title: 'Email Client Preview',
      description: 'Test your emails across Gmail, Outlook, and more',
      icon: <EmailIcon fontSize="large" />,
    },
    {
      title: 'Responsive Testing',
      description: 'Preview your emails on different devices and screen sizes',
      icon: <DevicesIcon fontSize="large" />,
    },
    {
      title: 'Performance Analysis',
      description: 'Get insights on email accessibility and performance',
      icon: <SpeedIcon fontSize="large" />,
    },
  ];

  const handleGetStarted = () => {
    if (user) {
      navigate('/app');
    } else {
      navigate('/app');  // We'll still navigate to app, where they'll see the login prompt
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #2A2F6F 0%, #0F1642 100%)',
        color: 'white',
        py: { xs: 8, md: 16 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 150%, rgba(42, 47, 111, 0.7) 0%, rgba(15, 22, 66, 0) 50%)',
          zIndex: 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          right: '-10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0) 70%)',
          borderRadius: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #E0E7FF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    mb: 3
                  }}
                >
                  Mailzen
                </Typography>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 4, 
                    opacity: 0.9,
                    color: '#E0E7FF',
                    lineHeight: 1.6
                  }}
                >
                  Design and test your email templates with confidence. Preview across all major email clients instantly.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #5558E6 0%, #7C4DEF 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 24px rgba(99, 102, 241, 0.3)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  {user ? 'Go to Dashboard' : 'Get Started Free'}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <Paper
                  elevation={6}
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: '24px',
                    transform: 'rotate(-2deg)',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: 'linear-gradient(90deg, #6366F1, #8B5CF6, #EC4899)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <EmailIcon sx={{ fontSize: 28, color: '#6366F1', mr: 1.5 }} />
                    <Typography variant="h6" sx={{ color: '#2A2F6F', fontWeight: 600 }}>
                      Preview your emails instantly
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex',
                    gap: 2,
                    mb: 3
                  }}>
                    <Box sx={{ 
                      width: '80px',
                      height: '80px',
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #F5F7FF 0%, #E0E7FF 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: '0 8px 16px rgba(99, 102, 241, 0.1)'
                    }}>
                      <DevicesIcon sx={{ fontSize: 40, color: '#6366F1' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: '#2A2F6F', fontWeight: 600 }}>
                        Multi-device Preview
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6B7280' }}>
                        Test your emails on mobile, tablet, and desktop views instantly
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ 
                    bgcolor: '#F5F7FF',
                    borderRadius: '16px',
                    p: 2.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5
                  }}>
                    <Box sx={{ 
                      height: '12px',
                      width: '80%',
                      bgcolor: '#E0E7FF',
                      borderRadius: '6px'
                    }} />
                    <Box sx={{ 
                      height: '12px',
                      width: '60%',
                      bgcolor: '#E0E7FF',
                      borderRadius: '6px'
                    }} />
                    <Box sx={{ 
                      height: '12px',
                      width: '70%',
                      bgcolor: '#E0E7FF',
                      borderRadius: '6px'
                    }} />
                  </Box>
                  <Box sx={{ 
                    display: 'flex',
                    gap: 1.5,
                    mt: 3,
                    justifyContent: 'flex-end'
                  }}>
                    {['Gmail', 'Outlook', 'Apple Mail'].map((client) => (
                      <Box
                        key={client}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: '8px',
                          background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          boxShadow: '0 4px 8px rgba(99, 102, 241, 0.2)'
                        }}
                      >
                        {client}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Comparison Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#F8FAFF' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            sx={{ 
              mb: 6,
              color: '#2A2F6F',
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Choose Your Plan
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {/* Free Plan */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: '24px',
                  border: '1px solid rgba(99, 102, 241, 0.1)',
                  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.05)',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  }
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ color: '#2A2F6F', fontWeight: 600 }}>
                  Free Plan
                </Typography>
                <Typography variant="h3" sx={{ color: '#6366F1', fontWeight: 700, mb: 3 }}>
                  $0
                  <Typography component="span" variant="body1" sx={{ color: '#6B7280' }}>/month</Typography>
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#6366F1' }} />
                    </ListItemIcon>
                    <ListItemText primary="Gmail & Outlook preview" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#6366F1' }} />
                    </ListItemIcon>
                    <ListItemText primary="Basic responsive testing" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#6366F1' }} />
                    </ListItemIcon>
                    <ListItemText primary="3 email templates" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#6366F1' }} />
                    </ListItemIcon>
                    <ListItemText primary="Basic spam score" />
                  </ListItem>
                </List>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={handleGetStarted}
                  sx={{
                    mt: 3,
                    borderColor: '#6366F1',
                    color: '#6366F1',
                    borderRadius: '12px',
                    '&:hover': {
                      borderColor: '#4F46E5',
                      bgcolor: 'rgba(99, 102, 241, 0.04)'
                    }
                  }}
                >
                  Get Started
                </Button>
              </Paper>
            </Grid>

            {/* Pro Plan */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderRadius: { xs: '16px', md: '24px' },
                  background: 'linear-gradient(135deg, #2A2F6F 0%, #0F1642 100%)',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)'
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    background: 'radial-gradient(circle at 100% 0%, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0) 50%)',
                    width: '300px',
                    height: '300px',
                    zIndex: 1
                  }
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    color: '#E0E7FF', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    Pro Plan
                  </Typography>
                  <Typography variant="h3" sx={{ 
                    color: 'white', 
                    fontWeight: 700, 
                    mb: 3,
                    fontSize: { xs: '2rem', md: '3rem' }
                  }}>
                    $19
                    <Typography component="span" variant="body1" sx={{ color: '#E0E7FF' }}>/month</Typography>
                  </Typography>
                  <List sx={{ py: 0 }}>
                    {[
                      'All email clients (Gmail, Outlook, Apple Mail, Yahoo, Thunderbird)',
                      'Advanced responsive testing with custom sizes',
                      'Unlimited email templates',
                      'AI-powered content analysis',
                      'Advanced spam analysis',
                      'Priority support'
                    ].map((feature, index) => (
                      <ListItem key={index} sx={{ py: { xs: 1, md: 1.5 } }}>
                        <ListItemIcon>
                          <CheckCircleIcon sx={{ color: '#8B5CF6' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              color: '#E0E7FF',
                              fontSize: { xs: '0.9rem', md: '1rem' },
                              lineHeight: { xs: 1.4, md: 1.6 }
                            } 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      mt: { xs: 2, md: 3 },
                      background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                      borderRadius: '12px',
                      py: { xs: 1.5, md: 2 },
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      '&:hover': {
                        background: 'linear-gradient(90deg, #5558E6 0%, #7C4DEF 100%)'
                      }
                    }}
                  >
                    Upgrade to Pro
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2A2F6F',
            mb: 6
          }}
        >
          Features
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #F5F7FF 0%, #E0E7FF 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(99, 102, 241, 0.1)',
                    background: 'linear-gradient(135deg, #F8FAFF 0%, #E8EEFF 100%)'
                  }
                }}
              >
                <Box 
                  sx={{ 
                    color: '#6366F1',
                    mb: 3,
                    p: 2,
                    borderRadius: '16px',
                    background: 'white',
                    boxShadow: '0 8px 16px rgba(99, 102, 241, 0.1)'
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#2A2F6F',
                    mb: 2
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    fontSize: '1.1rem',
                    lineHeight: 1.6
                  }}
                >
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Pro Features Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #F5F7FF 0%, #E0E7FF 100%)',
        py: { xs: 8, md: 12 },
        mt: 8
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#2A2F6F',
              mb: 6
            }}
          >
            Pro Features
          </Typography>
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 3, md: 6 },
              borderRadius: '24px',
              background: 'white',
              boxShadow: '0 20px 40px rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.1)'
            }}
          >
            <Grid container spacing={6}>
              <Grid item xs={12} md={6}>
                <List>
                  {[
                    'All email clients (Apple Mail, Yahoo, Thunderbird, etc.)',
                    'Custom device sizes and responsive testing',
                    'Premium email templates and advanced features',
                    'Template management and organization',
                    'Advanced analytics and reporting',
                  ].map((feature, index) => (
                    <ListItem 
                      key={index}
                      sx={{
                        py: 1.5
                      }}
                    >
                      <ListItemIcon>
                        <CheckCircleIcon sx={{ color: '#6366F1', fontSize: 28 }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature}
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: '#2A2F6F',
                            fontSize: '1.1rem',
                            fontWeight: 500
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography 
                    variant="h4" 
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: '#2A2F6F',
                      mb: 2
                    }}
                  >
                    Upgrade to Pro
                  </Typography>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{
                      color: '#6366F1',
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    $19/month
                  </Typography>
                  <Typography 
                    paragraph 
                    sx={{
                      color: '#6B7280',
                      fontSize: '1.1rem',
                      mb: 4
                    }}
                  >
                    Get access to all features and take your email development to the next level
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={handleGetStarted}
                    sx={{
                      background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 100%)',
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: '12px',
                      boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #5558E6 0%, #7C4DEF 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 24px rgba(99, 102, 241, 0.3)',
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    Get Started
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}; 